import styled from '@emotion/styled';
import { useEffect } from 'react';
import { graphql, useFragment } from 'react-relay';

import type { FreeTextbookJsxLessonContainer_subtopic$key } from '__free_textbooks_generated__/FreeTextbookJsxLessonContainer_subtopic.graphql';
import JsxContent from 'ms-components/JsxContent';
import { PageTimeRecorder } from 'ms-helpers/PageTimeTracker';
import { useMaybeViewer } from 'ms-helpers/Viewer/Renderer';
import Switch from 'ms-pages/Teacher/components/Switch';
import { font } from 'ms-styles/base';
import { colors } from 'ms-styles/colors';
import { BASE_UNIT } from 'ms-styles/theme/Numero';
import AnchorButton from 'ms-ui-primitives/Button/AnchorButton';
import useQueryParam from 'ms-utils/hooks/useQueryParam';
import type { FixGeneratedKey } from 'ms-utils/relay/relay-compiler-language-typescript-fixes';

const RightAligned = styled.div({
  marginLeft: 'auto',
});

type Props = {
  subtopicKey: FixGeneratedKey<FreeTextbookJsxLessonContainer_subtopic$key>;
};

const FRAGMENT = graphql`
  fragment FreeTextbookJsxLessonContainer_subtopic on FreeTextbookSubtopic {
    rslJsx {
      id
      pdfAvailable
      pdfUrl
      transpiledJsx
      locale
    }
    rslTeacherNoteJsx {
      id
      transpiledJsx
      locale
    }
    investigationJsx {
      id
      transpiledJsx
      locale
    }
    investigationTeacherNoteJsx {
      id
      transpiledJsx
      locale
    }
    solidifyLessonJsx {
      id
      pdfAvailable
      transpiledJsx
      pdfUrl
    }
    solidifyTeacherNoteJsx {
      transpiledJsx
      pdfAvailable
      pdfUrl
    }
  }
`;

export function FreeTextbookJsxLessonContainer({ subtopicKey }: Props) {
  const {
    rslJsx,
    rslTeacherNoteJsx,
    investigationJsx,
    investigationTeacherNoteJsx,
    solidifyLessonJsx,
    solidifyTeacherNoteJsx,
  } = useFragment(FRAGMENT, subtopicKey);

  if (rslJsx.transpiledJsx != null) {
    return (
      <>
        <ContentHeading />
        <RSLContainer
          rsl={rslJsx.transpiledJsx}
          rslTeacherNote={rslTeacherNoteJsx?.transpiledJsx}
        />
      </>
    );
  }

  if (investigationJsx.transpiledJsx != null) {
    return (
      <>
        <ContentHeading />
        <RSLContainer
          rsl={investigationJsx.transpiledJsx}
          rslTeacherNote={investigationTeacherNoteJsx?.transpiledJsx}
        />
      </>
    );
  }

  return (
    <>
      <SolidifyLessonContainer
        solidifyLesson={solidifyLessonJsx.transpiledJsx}
        solidifyTeacherNote={solidifyTeacherNoteJsx.transpiledJsx}
        solidifyLessonPdfAvailable={solidifyLessonJsx.pdfAvailable}
        solidifyLessonPdfUrl={solidifyLessonJsx.pdfUrl}
        solidifyTeacherNotePdfAvailable={solidifyTeacherNoteJsx.pdfAvailable}
        solidifyTeacherNotePdfUrl={solidifyTeacherNoteJsx.pdfUrl}
      />
    </>
  );
}

function SolidifyLessonContainer({
  solidifyLesson,
  solidifyTeacherNote,
  solidifyLessonPdfUrl,
  solidifyLessonPdfAvailable,
  solidifyTeacherNotePdfUrl,
  solidifyTeacherNotePdfAvailable,
}: {
  solidifyLesson?: string | null | undefined;
  solidifyTeacherNote?: string | null | undefined;
  solidifyLessonPdfUrl?: string | null | undefined;
  solidifyLessonPdfAvailable?: boolean | undefined;
  solidifyTeacherNotePdfUrl?: string | null | undefined;
  solidifyTeacherNotePdfAvailable?: boolean | undefined;
}) {
  const { role } = useMaybeViewer() || { role: 'Other' };
  const selectedViewOptions = [
    { label: 'Student lesson', value: 'content' },
    { label: 'Teacher guide', value: 'teacherGuide' },
  ] as const;

  type SelectedSolidifyTab = 'content' | 'teacherGuide';
  const [activeSolidifyLessonTab, setActiveSolidifyLessonTab] =
    useQueryParam<SelectedSolidifyTab>(
      'activeSolidifyLessonTab',
      'content',
      (value): value is SelectedSolidifyTab =>
        value === 'content' || value === 'teacherGuide',
    );

  return (
    <PageTimeRecorder componentName="SolidifyLesson" pageName="Textbooks">
      <ContentHeading>
        <ContentTitle>Lesson</ContentTitle>
        <RightAligned>
          {activeSolidifyLessonTab === 'content' &&
            solidifyLessonPdfAvailable &&
            solidifyLessonPdfUrl != null && (
              <AnchorButton
                type="primary"
                href={solidifyLessonPdfUrl}
                target="_blank"
              >
                Download PDF
              </AnchorButton>
            )}
          {activeSolidifyLessonTab === 'teacherGuide' &&
            solidifyTeacherNotePdfUrl &&
            solidifyTeacherNotePdfAvailable != null && (
              <AnchorButton
                type="primary"
                href={solidifyTeacherNotePdfUrl}
                target="_blank"
              >
                Download PDF
              </AnchorButton>
            )}
        </RightAligned>
      </ContentHeading>
      {role === 'Teacher' && (
        <SwitchWrapper>
          <Switch<SelectedSolidifyTab>
            width={284}
            options={selectedViewOptions}
            value={activeSolidifyLessonTab}
            onChange={newValue => {
              setActiveSolidifyLessonTab(newValue);
            }}
          />
        </SwitchWrapper>
      )}
      {solidifyLesson != null &&
        (activeSolidifyLessonTab === 'content' || role === 'Other') && (
          <JsxContent transpiledJsx={solidifyLesson} />
        )}
      {solidifyTeacherNote != null &&
        activeSolidifyLessonTab === 'teacherGuide' &&
        role === 'Teacher' && (
          <JsxContent transpiledJsx={solidifyTeacherNote} />
        )}
    </PageTimeRecorder>
  );
}

function RSLContainer({
  rsl,
  rslTeacherNote,
}: {
  rsl: string;
  rslTeacherNote?: string | null | undefined;
}) {
  // TODO revist all usages of viewer/'role' in the FreeTextbook as theoretically
  // there should never be a viewer if the FreeTextbook is being rendered.
  const { role } = useMaybeViewer() || { role: 'Other' };
  const selectedViewOptions = [
    { label: 'Student lesson', value: 'content' },
    { label: 'Teacher guide', value: 'teacherGuide' },
  ] as const;

  type SelectedLessonTab = 'content' | 'teacherGuide';
  const [activeLessonTab, setActiveLessonTab] =
    useQueryParam<SelectedLessonTab>(
      'activeLessonTab',
      'content',
      (value): value is SelectedLessonTab =>
        value === 'content' || value === 'teacherGuide',
    );

  useEffect(() => {
    if (
      activeLessonTab === 'teacherGuide' &&
      (role !== 'Teacher' || rslTeacherNote == null)
    ) {
      setActiveLessonTab('content');
    }
  }, [activeLessonTab, role, rslTeacherNote, setActiveLessonTab]);

  return (
    <PageTimeRecorder componentName="RSL" pageName="Textbooks">
      {role === 'Teacher' && rslTeacherNote != null && (
        <SwitchWrapper>
          <Switch<SelectedLessonTab>
            width={284}
            options={selectedViewOptions}
            value={activeLessonTab}
            onChange={newValue => {
              setActiveLessonTab(newValue);
            }}
          />
        </SwitchWrapper>
      )}
      {(activeLessonTab === 'content' || role === 'Other') && (
        <JsxContent transpiledJsx={rsl} />
      )}
      {rslTeacherNote != null &&
        activeLessonTab === 'teacherGuide' &&
        role === 'Teacher' && <JsxContent transpiledJsx={rslTeacherNote} />}
    </PageTimeRecorder>
  );
}

const ContentHeading = styled.div({
  alignItems: 'center',
  display: 'flex',
  paddingTop: 6 * BASE_UNIT,
  paddingBottom: 6 * BASE_UNIT,
});

const SwitchWrapper = styled.div({
  alignItems: 'center',
  display: 'flex',
  paddingTop: 2 * BASE_UNIT,
  paddingBottom: 2 * BASE_UNIT,
});

const ContentTitle = styled.div({
  ...font.gilroyLargeHeading,
  color: colors.cloudBurst,
  lineHeight: '38px',
});
